import type {
  ScoreBoardData,
  ScoreBoardRanking,
  ServerCommandReplyMessage,
  ServerErrorMessage,
  ServerScoreDetailedBtrMessage,
  ServerScoreDetailedCtfMessage,
  ServerScoreDetailedMessage,
  ServerScoreUpdateMessage,
  ServerServerMessageMessage,
} from '@airmash/protocol';
import {
  COMMAND_REPLY_TYPES,
  decodeMinimapCoords,
  GAME_TYPES,
  MOB_TYPES,
  PLAYER_POWERUP_TYPES,
  PLAYER_UPGRADE_TYPES,
  SERVER_ERRORS,
  SERVER_MESSAGE_TYPES,
  SERVER_PACKETS,
} from '@airmash/protocol';
import type { Sprite } from 'pixi.js';
import { Graphics as PixiGraphics } from 'pixi.js';
import { ref } from 'valtio/vanilla';
import { EMOTE_NAMES, type EmoteName } from '../generated/emotes';
import type { FlagCode } from '../generated/flags';
import { UNKNOWN_FLAG, flagName, toServerFlag } from '../services/Flags';
import { escapePlayerName } from '../services/PlayerNames';
import {
  RADIO_CHANNELS,
  uiState,
  type ChatMessageEntry,
  type ChatMessageType,
  type CtfFlagAction,
  type CtfFlagTeam,
  type CtfPlaneRoster,
  type CtfPlaneRosterEntry,
  type DeathNoticePlayer,
  type GameNotification,
  type KillNoticeState,
  type LeaderboardEntry,
  type LeaderboardState,
  type MenuPage,
  type PanelType,
  type GameMode,
  type ScoreboardMvp,
  type ScoreboardPlayer,
} from '../ui/UIState';
import { viewportFloorMessage } from '../ui/airmash-ui/game/layout';
import { type AutoPilotFailure, cancelAutoPilot, startAutoPilot } from './AutoPilot';
import { clock } from './Clock';
import { GAME_DEFAULTS } from './Config';
import {
  ALT_SCALE_DEFAULT,
  HALF_PI,
  MAP_CENTER_X,
  MAP_CENTER_Y,
  MAP_HEIGHT,
  MAP_WIDTH,
  MAX_CHAT_LINES,
  MINIMAP_SCALE_DEFAULT,
  MINIMAP_SCALE_MAX,
  MINIMAP_SCALE_MIN,
  PINCH_SCALE_TOLERANCE,
  PlaneType,
  SCALE_DEFAULT,
  SCALE_MAX,
  SCALE_MIN,
} from './Constants';
import { device } from './Device';
import { SHIPS, UPGRADE_KINDS, type UpgradeKind, UPGRADES } from './GameData';
import { pushGameLogEntry } from './GameLog';
import { redirRoot as gamesRedirRoot, update as gamesUpdate, wipe as gamesWipe } from './Games';
import {
  applyScaleOnly as graphicsApplyScaleOnly,
  gui,
  minimapMob as graphicsMinimapMob,
  resizeRenderer as graphicsResizeRenderer,
  toggleFullscreen as graphicsToggleFullscreen,
  viewport,
} from './Graphics';
import { layers } from './Layers';
import {
  clearKeys as inputClearKeys,
  gameBlur as inputGameBlur,
  gameFocus as inputGameFocus,
  isAnyKeyHeld as inputIsAnyKeyHeld,
  toggleCruiseMode as inputToggleCruiseMode,
  toggleKeybinds as inputToggleKeybinds,
  toggleMouse as inputToggleMouse,
} from './Input';
import { isCtfMatchStart, notifyCtfMatchStarted, stopCtfMatchTimer } from './MatchTimer';
import { stripBotsNamePrefix } from './NameCleanup';
import {
  getScores as networkGetScores,
  receivedError as networkReceivedError,
  resizeHorizon as networkResizeHorizon,
  sendCommand as networkSendCommand,
  setup as networkSetup,
  spectateForce as networkSpectateForce,
  timing as networkTiming,
  voteMute as networkVoteMute,
} from './Network';
import type Player from './Player';
import {
  categoryCounts as playersCategoryCounts,
  count as playersCount,
  forEach as playersForEach,
  get as playersGet,
  getMe as playersGetMe,
} from './Players';
import { startProwlerAlert, stopProwlerAlert } from './ProwlerAlert';
import { radioChannelForKeyCode, radioHide, radioIsVisible, radioSendMessage, radioToggle } from './Radio';
import { GAME_STATE_CODES, session } from './Session';
import {
  playerUpgrade as soundPlayerUpgrade,
  playFlagVoice as soundPlayFlagVoice,
  powerup as soundPowerup,
  toggle as soundToggle,
} from './Sound';
import { setSettings, setSettingsUnsaved, settings } from './Settings';
import { armSpectatorOnStart, cancelSpectatorOnStart } from './SpectatorOnStart';
import { getNamed as texturesGetNamed, init as texturesInit, type TextureConfigName } from './Textures';
import { clamp as toolsClamp, colorLerp as toolsColorLerp, rankToEarnings as toolsRankToEarnings } from './Tools';
import type { BindName } from './types/Input';
import type { TeamId } from './types/Players';

/**
 * Minimap mob entry containing sprite and position data
 */
interface MinimapMob {
  /** Player id the dot belongs to */
  id: number;
  /** PIXI sprite rendered on minimap */
  sprite: Sprite;
  /** World X coordinate */
  x: number;
  /** World Y coordinate */
  y: number;
  /** Player's current rank (1-based) */
  rank: number;
  /** Player is a bot, as of the last scoreboard packet; drives the bot dot styling */
  bot: boolean;
  /** performance.now() of the last scoreboard update for this player; drives the
   * age-fade so dots whose data is going stale visibly dim before the next
   * scoreboard packet refreshes them. */
  lastUpdated: number;
}

/**
 * Minimap dot fade curve. The server's scoreboard packet arrives every ~5s,
 * so a dot's reported position can be up to that old. We render fully opaque
 * for the first second, then fade linearly to a floor over the rest of the
 * window. The next scoreboard tick snaps it back to full.
 */
const MINIMAP_FADE_FULL_MS = 1000;
const MINIMAP_FADE_OUT_MS = 5000;
const MINIMAP_FADE_FLOOR = 0.4;

/** Texture names for top 3 players on minimap (badge icons) */
const MINIMAP_RANK_TEXTURES: Record<number, TextureConfigName> = {
  1: 'minimapBadgeGold',
  2: 'minimapBadgeSilver',
  3: 'minimapBadgeBronze',
};

/** The minimap's screen layout: its inset from the bottom-right corner, its base size and the player's zoom. */
export interface MinimapLayout {
  paddingX: number;
  paddingY: number;
  size: number;
  scale: number;
}
const MINIMAP_PADDING_DEFAULT = 16;
const MINIMAP_SIZE_DEFAULT = 240;
/**
 * The layout this module sizes: in resizeMinimap and the zoom functions, and
 * for a phone or tablet in applyMobileLayout. Writes go through this binding;
 * reads, this module's included, through the read-only view below.
 */
const minimapLayoutState: MinimapLayout = {
  paddingX: MINIMAP_PADDING_DEFAULT,
  paddingY: MINIMAP_PADDING_DEFAULT,
  size: MINIMAP_SIZE_DEFAULT,
  scale: MINIMAP_SCALE_DEFAULT,
};
export const minimapLayout: Readonly<MinimapLayout> = minimapLayoutState;
/** How many scoreboard rows to show; fewer on a phone or tablet, see applyMobileLayout. */
let maxScoreboard = 100;

/** Minimap mobs by player id. A Map so the per-frame fade pass iterates without
 *  rebuilding a key list (a number-keyed object with deletes goes dictionary-mode). */
const minimapMobs = new Map<number, MinimapMob>();
/** Player ids present in the current scoreboard packet; reused across ticks. */
const liveMobIds = new Set<number>();

/**
 * Ring sprite drawn around the currently-tracked player's minimap dot. Single
 * instance (single-target tracking). Created/destroyed by `updateTrackedSprite`
 * as `uiState.trackedPlayerId` changes or the tracked player drops off the
 * minimap (leave, spectate, game switch).
 */
let trackedMinimapSprite: Sprite | null = null;
const ignoredPlayerIds = new Set<number>();
let delayedGraphicsResizeTimer: number | undefined;
let chatLineId = 0;
let getScoresIntervalId: number | null = null;
let applyPowerupTimer: number | null = null;
let powerupSeqId = 0;
let lastPrivateMessage: string | null = null;

/**
 * Each HUD arc is drawn into its own render texture. The arcs are sprite-masked,
 * which Pixi routes through its alpha-mask filter path, so redrawing them every
 * frame cost two masked passes for an image that only changes when health or
 * energy move. updateHUD flags an arc stale when its inputs change and the
 * render loop redraws only flagged arcs.
 */
let hudEnergyStale = true;
let hudHealthStale = true;
let lastHudHealth = -1;
let lastHudEnergy = -1;
let lastHudEnergyTint = -1;

/** Reads and clears the energy arc's stale flag. */
export function takeHudEnergyStale(): boolean {
  const stale = hudEnergyStale;
  hudEnergyStale = false;
  return stale;
}

/** Reads and clears the health arc's stale flag. */
export function takeHudHealthStale(): boolean {
  const stale = hudHealthStale;
  hudHealthStale = false;
  return stale;
}

/** Flags both arcs for a redraw: after setup, a visibility change, or a restored GL context. */
export function markHudStale(): void {
  hudEnergyStale = true;
  hudHealthStale = true;
}

/** Death notice data shown when player dies or kills */
type DeathNotice =
  | false
  | {
      type: 'killed' | 'destroyed';
      players: DeathNoticePlayer[];
      duration: number;
      /** A kill that started a new streak (victim list reset), so a notice still
       *  on screen is replaced rather than folded into. */
      freshStreak: boolean;
    };

let playerStats = {
  upgrades: -1,
  earnings: -1,
  kills: -1,
  deaths: -1,
};
let deathNotice: DeathNotice = false;
let deathNoticeSeq = 0;
let gameNotificationSeq = 0;

/** Shows a game notification; the id lets the caller clear that notice later without touching a newer one. */
export function setGameNotification(notification: Omit<GameNotification, 'id'>): number {
  const id = ++gameNotificationSeq;
  uiState.gameNotification = { id, ...notification };
  return id;
}

/** Clears the notification with this id if it is still the one showing; a later one stays. */
export function clearGameNotification(id: number): void {
  if (uiState.gameNotification?.id === id) uiState.gameNotification = null;
}
/** Display names for upgrade notification messages, indexed by UpgradeKind */
const UPGRADE_DISPLAY_NAMES: Readonly<Record<UpgradeKind, string>> = {
  speed: 'Speed',
  defense: 'Defense',
  energy: 'Energy Regen',
  missile: 'Missile Speed',
};

const DEFAULT_UPGRADES: Readonly<Record<UpgradeKind, number>> = {
  speed: 0,
  defense: 0,
  energy: 0,
  missile: 0,
};

let playerUpgrades: Record<UpgradeKind, number> = { ...DEFAULT_UPGRADES };
let listOfPlayersKills: DeathNoticePlayer[] = [];
let lastTimePlayerWasKilled = 0;

const EMOTE_SET: ReadonlySet<string> = new Set(EMOTE_NAMES);

/** Whether a bare word is one of the generated emote names. */
function isEmoteName(word: string): word is EmoteName {
  return EMOTE_SET.has(word);
}

/**
 * Checks if a word is a valid emote name.
 * @param word - The word to check (e.g. "lol", ":lol:" or "-lol-")
 * @param wrapped - If true, expects a delimiter-wrapped SAY token: colon-wrapped
 *   ":emote:" (vanilla dialect) or dash-wrapped "-emote-" (what live StarMash
 *   sends; it accepts both on receive, and so do we). Otherwise bare name.
 * @returns The emote name (without delimiters) if valid, false otherwise
 */
export function isEmote(word: string, wrapped: boolean): EmoteName | false {
  if (wrapped) {
    const delim = word[0];
    if ((delim === ':' || delim === '-') && word.endsWith(delim)) {
      const name = word.slice(1, -1);
      return isEmoteName(name) ? name : false;
    }
    return false;
  }
  return isEmoteName(word) ? word : false;
}

/**
 * BRITTLE: CTF flag event pattern for parsing SERVER_MESSAGE HTML
 *
 * The server sends CTF flag events as HTML in SERVER_MESSAGE packets.
 * Format: `<span class="info inline"><span class="(blueflag|redflag)"></span></span>(Captured|Returned|Taken) by [PlayerName]`
 *
 * If the server changes this format, this regex will silently fail to match
 * and CTF events won't appear in the game log (but will still show as messages).
 *
 * Groups:
 * - [1]: Full prefix (for sanitization reuse)
 * - [2]: Flag team class ("blueflag" or "redflag")
 * - [3]: Action ("Captured", "Returned", or "Taken")
 * - [4]: Player name (may contain HTML entities)
 */
const CTF_FLAG_PATTERN =
  /^(<span class="info inline"><span class="(blueflag|redflag)"><\/span><\/span>(Captured|Returned|Taken) by )(.*)$/m;

/**
 * Parses a CTF flag event from SERVER_MESSAGE HTML, plays the flag voice, and
 * adds captures and returns to the game log.
 *
 * BRITTLE: This relies on parsing HTML text from the server. If the server
 * changes the message format, this will silently stop working.
 *
 * @param html - Raw HTML message from server
 */
function parseCtfFlagEvent(html: string): void {
  const result = CTF_FLAG_PATTERN.exec(html);
  if (!result) {
    return;
  }

  const flagClass = result[2]!; // "blueflag" or "redflag"
  const actionText = result[3]!; // "Captured", "Returned", or "Taken"
  const rawPlayerName = result[4]!;

  // Parse action (lowercase for type safety)
  const action = actionText.toLowerCase() as CtfFlagAction;

  // Parse flag team from class name
  const flagTeam: CtfFlagTeam = flagClass === 'blueflag' ? 'blue' : 'red';

  // Voice commentary fires for ALL three actions (taken/captured/returned),
  // under its own setting (voiceEventsCtf, checked in playFlagVoice). It runs
  // before the early return below: the feed entry for "taken" is suppressed
  // because it's noisy in text, but the voice is the whole point. The log's
  // own setting is applied by the push.
  const me = playersGetMe();
  if (me) {
    const flagTeamNum = flagTeam === 'blue' ? 1 : 2;
    soundPlayFlagVoice(action, me.team === flagTeamNum);
  }

  // Only log "captured" and "returned" events, skip "taken" (too noisy)
  if (action === 'taken') {
    return;
  }

  // Clean up player name (strip bot prefix, decode HTML entities)
  const playerName = stripBotsNamePrefix(decodeHTMLEntities(rawPlayerName));

  // CTF rule: capturing = scoring the ENEMY flag (player is the opposite
  // team), returning = bringing YOUR flag back to base (player is same team).
  const playerTeam: CtfFlagTeam = action === 'captured' ? (flagTeam === 'blue' ? 'red' : 'blue') : flagTeam;

  pushGameLogEntry({ type: 'ctf-flag', action, flagTeam, playerTeam, playerName });
}

/** Extracts plain text title and message from server HTML for game-notification display. */
function parseServerMessageText(html: string): { title: string; message: string } {
  const result = CTF_FLAG_PATTERN.exec(html);
  if (result) {
    const action = result[3]!;
    const name = stripBotsNamePrefix(decodeHTMLEntities(result[4]!));
    return { title: action.toUpperCase(), message: `by ${name}` };
  }
  // Strip HTML tags, convert <br> to newlines, then decode entities so escaped
  // player names in server text ("Assisted in killing NCAF&#x27;s Helper")
  // render as characters — the Lit notification prints this as a text node.
  const plain = decodeHTMLEntities(html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, ''));
  return { title: '', message: plain };
}

/** Legacy showed a server message for 2s when the packet carried no duration. */
const SERVER_MESSAGE_DEFAULT_MS = 2000;

/** Displays a server message as a game notification. */
export function serverMessage(msg: Pick<ServerServerMessageMessage, 'type' | 'text' | 'duration'>): void {
  parseCtfFlagEvent(msg.text);
  if (isCtfMatchStart(msg)) {
    notifyCtfMatchStarted();
    armSpectatorOnStart(inputIsAnyKeyHeld);
  }

  const type = msg.type === SERVER_MESSAGE_TYPES.INFO ? 'information' : 'alert';
  const { title, message } = parseServerMessageText(msg.text);
  setGameNotification({
    type,
    title,
    message,
    showReconnect: false,
    timeoutMs: msg.duration || SERVER_MESSAGE_DEFAULT_MS,
  });
}

/** Clears all visible Lit-based notifications. */
function wipeAllMessages() {
  uiState.gameNotification = null;
  uiState.killNotice = null;
  uiState.deathNotice = null;
}

/** Updates the player's level in game state and UIState. */
export function updateMyLevel(newLevel: number) {
  session.myLevel = newLevel;
  uiState.level = newLevel;
}

/**
 * The kill notice while it is still up to be added to: one that has started
 * its fade-out is left to finish, since a change to it would bring it back and
 * read as a second kill. The component reports the fade through
 * GameAPI.markKillNoticeFading as it starts.
 */
function liveKillNotice(): KillNoticeState | null {
  const notice = uiState.killNotice;
  return notice !== null && !notice.fading ? notice : null;
}

/** Processes a server score update — syncs game state, UIState, and dispatches kill/death notices. */
export function newScore(scoreUpdate: ServerScoreUpdateMessage): void {
  if (scoreUpdate.id !== session.myID) return;

  let scoreChange: number | false = scoreUpdate.score - session.myScore;
  let pendingUpgrades = 0;

  if (Math.abs(scoreChange) < 1) scoreChange = false;

  // Sync score
  session.myScore = scoreUpdate.score;
  uiState.score = scoreUpdate.score;

  // Sync upgrades
  if (scoreUpdate.upgrades !== playerStats.upgrades) {
    const upgradesChange = scoreUpdate.upgrades - playerStats.upgrades;
    const hadUpgrades = playerStats.upgrades !== -1;
    playerStats.upgrades = scoreUpdate.upgrades;
    uiState.availableUpgrades = scoreUpdate.upgrades;

    // Fold upgrade *losses* (e.g. dropping one) into a live notice too, so its
    // running "+N upgrades" tally stays net-accurate — otherwise dropping then
    // re-collecting inflates it (shows +2 while you hold 1). Clamp at 0 so the
    // line simply disappears once a gain is fully cancelled. Gains are folded
    // below, alongside the pickup sound.
    if (hadUpgrades && upgradesChange < 0) {
      const live = liveKillNotice();
      if (live) live.upgradesChange = Math.max(0, live.upgradesChange + upgradesChange);
    }

    if (hadUpgrades && upgradesChange > 0) {
      soundPowerup(MOB_TYPES.UPGRADE, null);

      // Fold into the kill notice while it is live; past its fade, a fresh notice is made below
      const live = liveKillNotice();
      if (live) {
        live.upgradesChange += upgradesChange;
        return;
      }

      if (deathNotice) {
        pendingUpgrades += upgradesChange;
      } else {
        // No early return here: the lifetime stats below still sync, and a score
        // change in the same packet folds into this notice, as legacy showed
        // "+N upgrade" and "+score" together.
        uiState.killNotice = {
          id: ++deathNoticeSeq,
          players: [],
          scoreChange: false,
          upgradesChange: upgradesChange,
          timeoutMs: 3000,
          fading: false,
        };
      }
    }
  }

  // Sync lifetime stats
  if (scoreUpdate.earnings !== playerStats.earnings && session.myLevel !== 0) {
    playerStats.earnings = scoreUpdate.earnings;
    const nextXp = Math.ceil(toolsRankToEarnings(session.myLevel + 1) - scoreUpdate.earnings);
    uiState.lifetimeStats.earnings = scoreUpdate.earnings;
    uiState.lifetimeStats.nextLevelXp = nextXp;
  }
  if (scoreUpdate.totalkills !== playerStats.kills) {
    playerStats.kills = scoreUpdate.totalkills;
    uiState.lifetimeStats.kills = scoreUpdate.totalkills;
  }
  if (scoreUpdate.totaldeaths !== playerStats.deaths) {
    playerStats.deaths = scoreUpdate.totaldeaths;
    uiState.lifetimeStats.deaths = scoreUpdate.totaldeaths;
  }

  // Dispatch kill/death/score notification
  if (deathNotice) {
    if (deathNotice.type === 'killed') {
      const existing = liveKillNotice();
      // A kill inside the streak window folds into the notice on screen and the
      // score keeps summing. A fresh streak replaces it (new id, new timer), so
      // the sum can't span victims the notice no longer lists.
      if (existing && !deathNotice.freshStreak) {
        existing.players = deathNotice.players;
        if (scoreChange) {
          const prev = existing.scoreChange;
          existing.scoreChange = (prev !== false ? prev : 0) + scoreChange;
        }
        existing.upgradesChange += pendingUpgrades;
      } else {
        uiState.killNotice = {
          id: ++deathNoticeSeq,
          players: deathNotice.players,
          scoreChange: scoreChange || false,
          upgradesChange: pendingUpgrades,
          timeoutMs: deathNotice.duration,
          fading: false,
        };
      }
    } else {
      uiState.deathNotice = {
        id: ++deathNoticeSeq,
        killer: deathNotice.players[0]!,
        scoreChange: scoreChange || false,
        upgradesChange: pendingUpgrades,
        timeoutMs: deathNotice.duration,
      };
    }
    deathNotice = false;
  } else if (scoreChange) {
    // Points with no kill attached, an assist or a flag: they join a live kill
    // notice's tally, and once that notice has started to fade they show on
    // their own, as legacy's did, rather than bringing the faded kill back
    const live = liveKillNotice();
    if (live) {
      const current = live.scoreChange;
      live.scoreChange = (current !== false ? current : 0) + scoreChange;
    } else {
      uiState.killNotice = {
        id: ++deathNoticeSeq,
        players: [],
        scoreChange: scoreChange,
        upgradesChange: 0,
        timeoutMs: 3000,
        fading: false,
      };
    }
  }
}

/** Tint applied to bot minimap dots in FFA — desaturated grey so humans pop. */
const BOT_FFA_TINT = 0x808080;
/** Multiplier applied to bot dot size — visually de-emphasizes bots in CTF/FFA. */
const BOT_SCALE_FACTOR = 0.6;

/** Removes the tracked-player ring sprite from the minimap, if any. */
function destroyTrackedSprite() {
  if (trackedMinimapSprite === null) return;
  const parent = trackedMinimapSprite.parent;
  if (parent) parent.removeChild(trackedMinimapSprite);
  trackedMinimapSprite.destroy();
  trackedMinimapSprite = null;
}

/**
 * Syncs the tracked-player ring with `uiState.trackedPlayerId`. Creates/positions
 * the sprite over the tracked mob's dot, destroys it when tracking is off, and
 * auto-clears `trackedPlayerId` if the tracked mob isn't live (player left,
 * went to spectate, game switched). Keeps the sprite above the badge sprites
 * that the scoreboard handler reorders.
 */
function updateTrackedSprite() {
  const trackedId = uiState.trackedPlayerId;
  if (trackedId === null) {
    destroyTrackedSprite();
    return;
  }
  const mob = minimapMobs.get(trackedId);
  if (!mob) {
    // No live minimap dot for this id. Could be temporary (dead/respawning —
    // still in playersById) or permanent (left the game / spectate / wipe).
    // Only clear the tracking in the permanent case; otherwise hide the ring
    // and let it reappear when they respawn.
    if (!playersGet(trackedId)) {
      uiState.trackedPlayerId = null;
    }
    destroyTrackedSprite();
    return;
  }
  if (trackedMinimapSprite === null) {
    trackedMinimapSprite = texturesInit('minimapTracked');
  }
  trackedMinimapSprite.position.set(mob.sprite.position.x, mob.sprite.position.y);
  // powerup_circle is a full-plane-size glow sprite, much larger than ui_minimap_mob.
  // Render at a small fraction so the ring sits *around* the dot, not over the whole map.
  trackedMinimapSprite.scale.set(0.08 * minimapLayout.scale);
  // Re-adding moves a child to the top of the render order, but it also marks the
  // scene structure dirty, so only do it when the ring isn't already on top.
  const ui1 = layers.ui1;
  if (ui1.children[ui1.children.length - 1] !== trackedMinimapSprite) {
    ui1.addChild(trackedMinimapSprite);
  }
}

/**
 * Positions and sizes a minimap dot from its world coordinates, bot treatment
 * included, so the scoreboard tick, a resize and a zoom all land on the same
 * result (the bot sizing used to be a separate pass that resizes skipped).
 *
 * Trophies (top 3) keep 0.6× so the medals stay visible; regular dots are
 * dialled down to 0.5× because the full-sized red mob sprites read as awkward
 * and crowded on a minimap that's already showing the see-through viewport
 * box and the white self-dot.
 *
 * Bots, with the setting on and no trophy, are de-emphasized per mode:
 * - **CTF**: 0.6× size; the team colour stays so blue/red still reads.
 * - **FFA**: 0.6× size and a grey tint, so humans visually pop.
 * - **BTR**: no flagged bots; the size cut would apply if one appeared.
 */
function updateMinimapMob(mob: MinimapMob) {
  graphicsMinimapMob(mob.sprite, mob.x, mob.y);

  const isTopThree = mob.rank <= 3;
  const baseScale = isTopThree ? 0.2 : 0.8;
  const sizeFactor = isTopThree ? 0.6 : 0.5;
  let scale = baseScale * minimapLayout.scale * sizeFactor;
  let tint = 0xffffff;
  if (mob.bot && !isTopThree && (settings.game?.botsColor ?? GAME_DEFAULTS.botsColor)) {
    scale *= BOT_SCALE_FACTOR;
    if (session.gameType === GAME_TYPES.FFA) {
      tint = BOT_FFA_TINT;
    }
  }
  mob.sprite.scale.set(scale);
  mob.sprite.tint = tint;
}

/**
 * Per-frame age-fade pass. The scoreboard packet only arrives every ~5s, so
 * a dot can be that old. We hold full opacity for `MINIMAP_FADE_FULL_MS`
 * after the last refresh, then linearly fade to `MINIMAP_FADE_FLOOR` over
 * the remainder of the window. The next scoreboard tick snaps `mob.sprite.alpha`
 * back to 1 (see scoreboardUpdate).
 */
export function updateMinimapMobAges() {
  const now = performance.now();
  for (const mob of minimapMobs.values()) {
    const age = now - mob.lastUpdated;
    if (age <= MINIMAP_FADE_FULL_MS) {
      if (mob.sprite.alpha !== 1) mob.sprite.alpha = 1;
      continue;
    }
    const fadeWindow = MINIMAP_FADE_OUT_MS - MINIMAP_FADE_FULL_MS;
    const t = (age - MINIMAP_FADE_FULL_MS) / fadeWindow;
    const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
    mob.sprite.alpha = 1 - (1 - MINIMAP_FADE_FLOOR) * clamped;
  }
}

/** Removes and destroys all minimap mob sprites, and the tracked ring with them. */
function wipeAllMinimapMobs() {
  destroyTrackedSprite();

  const ui1 = layers.ui1;

  for (const mob of minimapMobs.values()) {
    ui1.removeChild(mob.sprite);
    mob.sprite.destroy();
  }
  minimapMobs.clear();
}

/** Shows the spectator overlay. */
export function showSpectator(mode: 'enter' | 'spectating', playerName = '') {
  uiState.spectator = { mode, playerName };
}

/** Hides the spectator overlay. */
export function hideSpectator() {
  uiState.spectator = null;
}

/** Displays a powerup countdown progress bar. */
export function addPowerup(type: PLAYER_POWERUP_TYPES, duration: number) {
  if (applyPowerupTimer != null) clearTimeout(applyPowerupTimer);

  uiState.activePowerup = {
    id: ++powerupSeqId,
    type: type === PLAYER_POWERUP_TYPES.SHIELD ? 'shield' : 'inferno',
    durationMs: duration,
  };

  applyPowerupTimer = window.setTimeout(() => {
    uiState.activePowerup = null;
  }, duration);
}

/** Clears the active powerup display. */
export function resetPowerups() {
  if (applyPowerupTimer != null) {
    clearTimeout(applyPowerupTimer);
    applyPowerupTimer = null;
  }
  uiState.activePowerup = null;
}

/** Updates the minimap sprite texture for a player based on their team. */
export function changeMinimapTeam(id: number, team: TeamId) {
  const mob = minimapMobs.get(id);
  if (!mob) return;

  // Top 3 keep their medal texture
  if (mob.rank > 3) {
    mob.sprite.texture = texturesGetNamed(team === 1 ? 'minimapBlue' : 'minimapMob');
  }
}

/** Plane types with a CTF roster, in selector order. */
const ROSTER_PLANE_TYPES: readonly PlaneType[] = [
  PlaneType.Predator,
  PlaneType.Goliath,
  PlaneType.Mohawk,
  PlaneType.Tornado,
  PlaneType.Prowler,
];

/** Roster sort; hoisted so the per-tick sorts don't allocate a comparator. */
function byRosterName(a: CtfPlaneRosterEntry, b: CtfPlaneRosterEntry) {
  return a.name.localeCompare(b.name);
}

function rosterNamesEqual(a: CtfPlaneRosterEntry[], b: CtfPlaneRosterEntry[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.name !== b[i]!.name) return false;
  }
  return true;
}

/** True when every plane's blue and red name lists match. */
function rostersEqual(
  prev: Partial<Record<PlaneType, CtfPlaneRoster>> | null,
  next: Partial<Record<PlaneType, CtfPlaneRoster>>,
) {
  if (prev === null) return false;
  for (const type of ROSTER_PLANE_TYPES) {
    const a = prev[type];
    const b = next[type];
    if (!a || !b) return false;
    if (!rosterNamesEqual(a.blue, b.blue) || !rosterNamesEqual(a.red, b.red)) return false;
  }
  return true;
}

/** True when the leaderboard would render identically. */
function leaderboardEquals(prev: LeaderboardState | null, next: LeaderboardState) {
  if (prev === null) return false;
  if (prev.showEllipsisBefore !== next.showEllipsisBefore || prev.showEllipsisAfter !== next.showEllipsisAfter) {
    return false;
  }
  if (prev.entries.length !== next.entries.length) return false;
  for (let i = 0; i < next.entries.length; i++) {
    const a = prev.entries[i]!;
    const b = next.entries[i]!;
    if (
      a.id !== b.id ||
      a.rank !== b.rank ||
      a.flag !== b.flag ||
      a.name !== b.name ||
      a.team !== b.team ||
      a.level !== b.level ||
      a.score !== b.score ||
      a.isBot !== b.isBot ||
      a.isSpectating !== b.isSpectating ||
      a.isMe !== b.isMe
    ) {
      return false;
    }
  }
  return true;
}

/** Updates minimap positions and scoreboard display from server data. */
export function scoreboardUpdate(msgData: ScoreBoardData[], msgRankings: ScoreBoardRanking[]) {
  const now = performance.now();
  const medalMobs: MinimapMob[] = [];
  liveMobIds.clear();
  let myRank = 0;

  for (let i = 0; i < msgRankings.length; i++) {
    const ranking = msgRankings[i];
    if (!ranking) continue;

    const player = playersGet(ranking.id);
    if (!player) continue;

    const pos = decodeMinimapCoords(ranking.x, ranking.y);
    player.lowResPos.x = pos.x;
    player.lowResPos.y = pos.y;

    const rank = i + 1;
    player.setRank(rank);

    if (ranking.id === session.myID) {
      myRank = rank;
      continue;
    }

    if (ranking.x !== 0 || ranking.y !== 0) {
      liveMobIds.add(player.id);

      const isTopThree = rank <= 3;
      const isBlueTeam = GAME_TYPES.CTF === session.gameType && player.team === 1;
      const textureName = isTopThree
        ? (MINIMAP_RANK_TEXTURES[rank] ?? 'minimapMob')
        : isBlueTeam
          ? 'minimapBlue'
          : 'minimapMob';

      let mob = minimapMobs.get(ranking.id);
      if (!mob) {
        mob = {
          id: ranking.id,
          sprite: texturesInit(textureName),
          x: pos.x,
          y: pos.y,
          rank,
          bot: player.bot,
          lastUpdated: now,
        };
        minimapMobs.set(ranking.id, mob);
      } else {
        mob.x = pos.x;
        mob.y = pos.y;
        mob.bot = player.bot;
        mob.lastUpdated = now;

        // Swap texture if rank changed (different badge or crossed top-3 boundary)
        if (rank !== mob.rank) {
          mob.sprite.texture = texturesGetNamed(textureName);
        }

        mob.rank = rank;
      }

      mob.sprite.alpha = 1;
      updateMinimapMob(mob);
      if (isTopThree) medalMobs.push(mob);
    } else {
      /*
       * If a player is missing from the minimap for longer than the 3s
       * post-death duration (PLAYERS_DEATH_INACTIVITY_MS in ab-server)
       * after their kill, then we assume the player is spectating.
       *
       * We have knowledge of these last-killed times because all player
       * kills are broadcast to all players. However, if we join a game
       * and a player has been killed just before that, we don't know if
       * they are spectating or just awaiting revival.
       *
       * So we assume they are spectating, by having the lastKilled
       * property initialized to zero (rather than performance.now()) in
       * Player constructor. If this is wrong, it will be corrected within
       * a few seconds, when they respawn/revive.
       */
      if (now - player.lastKilled > 3000) {
        player.spectate = true;
      }
    }
  }

  const ui1 = layers.ui1;
  for (const [id, mob] of minimapMobs) {
    if (liveMobIds.has(id)) continue;
    ui1.removeChild(mob.sprite);
    mob.sprite.destroy();
    minimapMobs.delete(id);
  }

  // Bring the medal sprites to the front (re-adding moves a child to the top
  // of the render order), lowest player id first as the old key order did.
  medalMobs.sort((a, b) => a.id - b.id);
  for (const mob of medalMobs) ui1.addChild(mob.sprite);

  // Sync tracked-player ring last so it sits above everything else.
  updateTrackedSprite();

  if (!myRank) return;

  const maxEntries = maxScoreboard ? toolsClamp(maxScoreboard, 1, msgData.length) : msgData.length;
  const entries: LeaderboardEntry[] = [];
  let displayRank = 0;
  let endOfBoard = false;
  let showEllipsisBefore = false;

  for (let i = 0; i < maxEntries && !endOfBoard; i++) {
    const scoreData = msgData[i];
    if (!scoreData) continue;

    let player = playersGet(scoreData.id);
    if (!player) continue;

    displayRank++;

    let entryRank = displayRank;
    let entryScore = scoreData.score;
    let entryLevel: number | 'bot' = player.bot ? 'bot' : scoreData.level;
    let isMe = player.me();

    if (myRank > maxEntries && displayRank === maxEntries - 1 && session.myID !== null) {
      showEllipsisBefore = true;
      const me = playersGet(session.myID);
      if (me) {
        player = me;
        entryRank = myRank;
        entryScore = session.myScore;
        entryLevel = session.myLevel;
        isMe = true;
        endOfBoard = true;
      }
    }

    entries.push({
      id: player.id,
      rank: entryRank,
      flag: player.flag,
      name: player.name,
      team: GAME_TYPES.CTF === session.gameType ? player.team : 0,
      level: entryLevel,
      score: entryScore,
      isBot: player.bot,
      isSpectating: player.isSpectating() && !player.bot,
      isMe,
    });
  }

  // A fresh object wakes every bound component, so publish only when a field
  // moved, as updateGameInfo does for gameStats.
  const leaderboard: LeaderboardState = {
    entries,
    showEllipsisBefore,
    showEllipsisAfter: !endOfBoard && msgData.length > maxEntries,
  };
  if (!leaderboardEquals(uiState.leaderboard, leaderboard)) {
    uiState.leaderboard = leaderboard;
  }

  // Per-plane-type rosters for the aircraft selector badges + tooltip in CTF.
  // Recomputed alongside the leaderboard (~every 5s when the server pushes
  // scoreboard data), which is fresh enough for an at-a-glance count.
  if (session.gameType === GAME_TYPES.CTF) {
    const rosters: Partial<Record<PlaneType, CtfPlaneRoster>> = {
      [PlaneType.Predator]: { blue: [], red: [] },
      [PlaneType.Goliath]: { blue: [], red: [] },
      [PlaneType.Mohawk]: { blue: [], red: [] },
      [PlaneType.Tornado]: { blue: [], red: [] },
      [PlaneType.Prowler]: { blue: [], red: [] },
    };
    playersForEach((p) => {
      if (p.bot || p.isSpectating()) return;
      const roster = rosters[p.type];
      if (!roster) return;
      const entry = { name: p.name };
      if (p.team === 1) roster.blue.push(entry);
      else if (p.team === 2) roster.red.push(entry);
    });
    for (const type of ROSTER_PLANE_TYPES) {
      const roster = rosters[type];
      if (!roster) continue;
      roster.blue.sort(byRosterName);
      roster.red.sort(byRosterName);
    }
    if (!rostersEqual(uiState.ctfPlaneRosters, rosters)) {
      uiState.ctfPlaneRosters = rosters;
    }
  } else {
    uiState.ctfPlaneRosters = null;
  }
}

/** Toggles the chat input box visibility. */
function toggleChatBox() {
  // Not on mobile, and not while the UI is hidden: the box would open unseen and take every key
  if (device.mobile || uiState.uiHidden) return;

  if (uiState.chat.isInputVisible) {
    uiState.chat.isInputVisible = false;
    uiState.chat.inputPrefill = null;
  } else {
    if (uiState.chat.isMinimized) {
      uiState.chat.isMinimized = false;
      uiState.chat.unreadCount = 0;
    }
    inputClearKeys(true);
    uiState.chat.isInputVisible = true;
  }
}

/** Opens the chat box with prefilled text (e.g. "/" for commands). */
export function shortcutChat(shortcut: string) {
  if (!uiState.chat.isInputVisible) {
    uiState.chat.inputPrefill = shortcut;
    toggleChatBox();
  }
}

/**
 * Appends to the chat panel, trims to the cap, and counts an unread line while minimized.
 *
 * The line goes in as a Valtio ref, as the game log's entries do: it is never
 * changed once pushed, so no proxy is built for it, the shift past the cap
 * moves plain objects, and a write elsewhere in uiState does not walk the
 * lines to refresh the chat's version. The array stays a proxy, so a push or
 * a shift still wakes the chat.
 */
function pushChatEntry(entry: ChatMessageEntry, countUnread: boolean) {
  uiState.chat.messages.push(ref(entry));
  if (uiState.chat.messages.length > MAX_CHAT_LINES) {
    uiState.chat.messages.shift();
  }
  if (countUnread && uiState.chat.isMinimized) {
    uiState.chat.unreadCount++;
  }
}

/** A line spoken by a player: public, whisper, team or proximity say. */
function pushPlayerLine(msg: Player, text: string, type: ChatMessageType) {
  chatLineId++;
  pushChatEntry(
    {
      id: chatLineId,
      type,
      player: {
        id: msg.id,
        name: msg.name,
        flag: msg.flag,
        flagLabel: getFlagLabel(msg.flag),
        team: msg.team,
      },
      text,
      isWarning: false,
      isFromMe: msg.id === session.myID,
    },
    true,
  );
}

/** Adds a player chat message to UIState. Ignores blocked players. */
export function addChatLine(msg: Player, text: string, type: ChatMessageType) {
  if (ignoredPlayerIds.has(msg.id)) return;

  if (type === 'whisper-from') {
    lastPrivateMessage = escapePlayerName(msg.name);
  }

  pushPlayerLine(msg, text, type);
}

/**
 * Adds a proximity SAY ("/s") message to the chat panel. The server only sends
 * CHAT_SAY for players within visible range, so receiving one means the speaker
 * is genuinely nearby — worth surfacing in chat in addition to the speech bubble.
 */
export function addSayLine(msg: Player, text: string) {
  if (ignoredPlayerIds.has(msg.id)) return;

  pushPlayerLine(msg, text, 'say');
}

/** Says when a flag goes to the server as the closest one it has: after a /flag, and at each login. */
export function noteFlagGate(flag: FlagCode): void {
  if (flag === UNKNOWN_FLAG) return;
  const serverFlag = toServerFlag(flag);
  if (serverFlag !== flag) {
    addChatMessage(
      `${flagName(flag)} is not on the server yet, so your flag shows as ${flagName(serverFlag)} until it is.`,
      false,
    );
  }
}

/** Adds a system message to the chat. Warning style by default. */
export function addChatMessage(text: string, isWarning = true) {
  chatLineId++;
  pushChatEntry(
    {
      id: chatLineId,
      type: 'system',
      player: null,
      text,
      isWarning,
      isFromMe: true,
    },
    false,
  );
}

/** How long to wait for our own SAY to come back before assuming it was
 *  dropped. Generous against a normal 20-40ms ping; a spike longer than this
 *  costs one wrong chat line, nothing more. */
const SAY_ECHO_TIMEOUT_MS = 2500;

let sayEchoTimerId: number | null = null;

/**
 * Arms the delivery watchdog for an emote or flag bubble.
 *
 * A SAY the server accepts always comes back to its sender: the broadcast pass
 * adds every player as a viewer of themselves before any viewport test
 * ("Player always sees itself", ab-server `collisions.ts`), and viewports live
 * from connect to disconnect — so the echo is guaranteed while connected,
 * alive or not. Silence therefore means the server dropped it, and for SAY it
 * never says why: the handler is `if (!isPlayerMuted(...)) broadcast` with no
 * else, and mutes are keyed by IP, so a shared VPN or relay address inherits
 * another player's mute. Without this the tap is simply silent, which is how
 * an inherited mute reads as "emotes are broken".
 */
export function watchSayEcho() {
  cancelSayEchoWatch();
  sayEchoTimerId = window.setTimeout(() => {
    sayEchoTimerId = null;
    addChatMessage(
      "Emote not sent — you may be muted, or sending them too fast. Shared VPN and relay addresses can inherit another player's mute.",
    );
  }, SAY_ECHO_TIMEOUT_MS);
}

/** Our own SAY came back, so the server accepted it. */
export function noteSayEcho(playerId: number) {
  if (playerId === session.myID) cancelSayEchoWatch();
}

/** Stops the watchdog. Also called when the server answers a send itself (its
 *  own wording beats ours) and on game teardown, so a pending timer can't
 *  surface a warning after leaving. */
export function cancelSayEchoWatch() {
  if (sayEchoTimerId !== null) {
    clearTimeout(sayEchoTimerId);
    sayEchoTimerId = null;
  }
}

/** Updates game stats and type in UIState for the Lit game-stats component. */
export function updateGameInfo() {
  const counts = playersCategoryCounts();

  uiState.ping = networkTiming.ping;
  // Every respawn on the server lands here, and most change no count. A fresh
  // object would notify every subscribed component anyway, so only write when
  // one moved.
  const stats = uiState.gameStats;
  if (
    stats.players !== counts.players ||
    stats.bots !== counts.bots ||
    stats.blueTeam !== counts.blueTeam ||
    stats.redTeam !== counts.redTeam ||
    stats.spectators !== counts.spectators
  ) {
    uiState.gameStats = {
      players: counts.players,
      bots: counts.bots,
      blueTeam: counts.blueTeam,
      redTeam: counts.redTeam,
      spectators: counts.spectators,
    };
  }

  if (session.gameType === GAME_TYPES.FFA) uiState.gameType = 'ffa';
  else if (session.gameType === GAME_TYPES.CTF) uiState.gameType = 'ctf';
  else if (session.gameType === GAME_TYPES.BTR) uiState.gameType = 'btr';
}

/**
 * Undoes the server's HTML escaping for text rendered as a DOM text node.
 *
 * SERVER_MESSAGE is the one protocol field defined as HTML, and the server
 * escapes player names inside it (CTF flag events, kill assists). The legacy
 * client pushed that straight into `innerHTML` so entities resolved for free;
 * Lit components print the same strings as text, which puts the escape code on
 * screen (`NCAF&#x27;s Helper`). `&amp;` is decoded last so a double-escaped
 * sequence (`&amp;lt;`) ends up as `&lt;` instead of `<`.
 */
export function decodeHTMLEntities(value: string) {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&(?:apos|#39|#x27|#X27);/g, "'")
    .replace(/&(?:#47|#x2F|#X2F);/g, '/')
    .replace(/&(?:#96|#x60|#X60);/g, '`')
    .replace(/&nbsp;/g, ' ')
    .replace(/&bull;/g, '•')
    .replace(/&amp;/g, '&');
}

/**
 * The box the renderer should fill.
 *
 * Prefers the VISUAL viewport, because iOS Safari resizes it without touching
 * the layout viewport - closing the last background tab drops the tab bar and
 * `innerHeight` never moves. Sized off `innerHeight` alone the game stays
 * built for chrome that is no longer on screen: the canvas is deliberately
 * `overdraw` larger than the viewport, so the revealed strip is real canvas
 * and world sprites (mountains, planes) draw into it, while the
 * viewport-sized terrain tiles stop at the old edge and leave a black band.
 * The Lit UI has always measured itself this way (`Responsive.ts`), so this
 * also stops the two layers disagreeing about where the viewport ends.
 *
 * Pinch-zoom also shrinks the visual viewport, so fall back to the layout
 * viewport above `ZoomGuard`'s threshold rather than rebuilding the renderer
 * for a gesture it is about to snap back.
 */
function gameViewportSize() {
  const viewport = window.visualViewport;

  if (!viewport || viewport.scale > PINCH_SCALE_TOLERANCE) {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  return { width: Math.round(viewport.width), height: Math.round(viewport.height) };
}

/** Only triggers resize if dimensions actually changed. */
function onWindowResize() {
  const { width, height } = gameViewportSize();

  if (width !== viewport.width || height !== viewport.height) {
    scheduleGraphicsResize(250);
    if (session.state === GAME_STATE_CODES.PLAYING) {
      noticeViewportFloor();
    }
  }
}

/** Whether the viewport-floor notice has been shown this session; once is enough. */
let viewportFloorNoticed = false;

/** Tells the player, once, that the viewport is under the floor the interface is made for (layout.ts). */
function noticeViewportFloor() {
  if (viewportFloorNoticed) return;
  const message = viewportFloorMessage(gameViewportSize(), uiState.isMobile);
  if (message === null) return;
  viewportFloorNoticed = true;
  setGameNotification({ type: 'information', title: '', message, showReconnect: false, timeoutMs: 8000 });
}

/** Visual-viewport resizes are browser chrome appearing or collapsing, EXCEPT
 *  while the chat input is focused - then it is the software keyboard, which
 *  halves the visible height. Rebuilding the renderer mid-message would be
 *  both expensive and wrong: mobile-chat rides the keyboard itself and the
 *  game underneath should keep its size. */
function onVisualViewportResize() {
  if (uiState.chat.isInputVisible) {
    return;
  }

  onWindowResize();
}

/** Spectator shortcut: the number row picks the ship to spawn with. */
const SPECTATOR_AIRCRAFT_BY_DIGIT: Readonly<Record<string, PlaneType>> = {
  Digit1: PlaneType.Predator,
  Digit2: PlaneType.Goliath,
  Digit3: PlaneType.Mohawk,
  Digit4: PlaneType.Tornado,
  Digit5: PlaneType.Prowler,
};

/** Radio panel: the number row sends the message at that index. */
const RADIO_INDEX_BY_DIGIT: Readonly<Record<string, number>> = {
  Digit1: 0,
  Digit2: 1,
  Digit3: 2,
  Digit4: 3,
  Digit5: 4,
  Digit6: 5,
  Digit7: 6,
  Digit8: 7,
  Digit9: 8,
  Digit0: 9,
};

/** Handles keyboard control input for game commands. */
export function controlKey(code: string, bindName: BindName | null): void {
  if (session.state !== GAME_STATE_CODES.PLAYING) return;
  if (uiState.keybinds.editingBindName != null) return;

  if (code === 'Enter') {
    toggleChatBox();
    return;
  }
  if (code === 'Escape') {
    if (radioIsVisible()) {
      radioHide();
      return;
    }
    if (uiState.chat.isInputVisible) toggleChatBox();
    closeAllPanels('');
    return;
  }

  // Spectator: number keys 1-5 spawn into the new match with the chosen
  // ship type (Predator/Goliath/Mohawk/Tornado/Prowler). One-handed shortcut
  // for spec viewers who want to jump in mid-match.
  if (session.spectatingID !== null && !radioIsVisible()) {
    const aircraft = SPECTATOR_AIRCRAFT_BY_DIGIT[code];
    if (aircraft !== undefined) {
      selectAircraft(aircraft);
      return;
    }
  }

  // Radio panel: intercept number keys when visible.
  // Also swallow UPGRADE1-4 keybinds so remapped upgrade keys (e.g. Q/W/E/R)
  // don't trigger upgrades while the radio is open.
  if (radioIsVisible()) {
    const index = RADIO_INDEX_BY_DIGIT[code];
    if (index !== undefined) {
      radioSendMessage(index);
      return;
    }
    if (bindName === 'UPGRADE1' || bindName === 'UPGRADE2' || bindName === 'UPGRADE3' || bindName === 'UPGRADE4') {
      return;
    }
  }

  // Radio channel keys, fixed as in StarMash. Outside CTF the team channel's
  // key falls through to whatever it is bound to.
  const radioChannel = radioChannelForKeyCode(code);
  if (radioChannel !== null && (!RADIO_CHANNELS[radioChannel].ctfOnly || session.gameType === GAME_TYPES.CTF)) {
    radioToggle(radioChannel);
    return;
  }

  if (code === 'Slash') {
    shortcutChat('/');
    return;
  }
  if (code === 'Hash' && session.gameType === GAME_TYPES.CTF) {
    shortcutChat('#');
    return;
  }

  switch (bindName) {
    case 'ALTZOOM':
      toggleScaleAltMode();
      break;
    case 'ZOOMIN':
      scaleIncrease();
      break;
    case 'ZOOMOUT':
      scaleDecrease();
      break;
    case 'DEFAULTZOOM':
      scaleDefault();
      break;
    case 'DROP':
      // Contextual drop: the flag in CTF, otherwise a powerup (FFA/BTR).
      if (session.gameType === GAME_TYPES.CTF) {
        dropFlag();
      } else {
        dropUpgrade();
      }
      break;
    case 'SHOWSCORE':
      toggleScore();
      break;
    case 'MAINMENU':
      if (uiState.activePanel === 'mainmenu') {
        setActivePanel(null);
      } else {
        openMainMenu();
      }
      break;
    case 'KEYBINDS':
      inputToggleKeybinds();
      break;
    case 'FULLSCREEN':
      graphicsToggleFullscreen();
      inputClearKeys();
      break;
    case 'MINIMIZECHAT':
      minimizeChat();
      break;
    case 'MAXIMIZECHAT':
      maximizeChat();
      break;
    case 'SAY':
      shortcutChat('/s ');
      break;
    case 'TEAM':
      if (session.gameType === GAME_TYPES.CTF) {
        shortcutChat('/t ');
      }
      break;
    case 'REPLY':
      if (lastPrivateMessage !== null) {
        shortcutChat(`/w ${lastPrivateMessage} `);
      }
      break;
    case 'SPECTATE':
      networkSpectateForce();
      break;
    case 'UPGRADE1':
      selectUpgrade(1);
      break;
    case 'UPGRADE2':
      selectUpgrade(2);
      break;
    case 'UPGRADE3':
      selectUpgrade(3);
      break;
    case 'UPGRADE4':
      selectUpgrade(4);
      break;
    case 'SOUND':
      soundToggle();
      break;
    case 'HELP':
      toggleHelp();
      break;
    case 'EMOTES':
      toggleEmotes();
      break;
    case 'SHOWGAMES':
      window.dispatchEvent(new CustomEvent('toggle-game-selector'));
      break;
    case 'LASER':
      toggleLaserPointer();
      break;
    case 'INVITE':
      toggleInvite();
      break;
    case 'MOUSEMODE':
      inputToggleMouse();
      break;
    case 'CRUISE':
      inputToggleCruiseMode();
      break;
    case 'HIDEUI':
      // An open chat box goes with the UI, or it would keep the keys while unseen
      if (!uiState.uiHidden && uiState.chat.isInputVisible) toggleChatBox();
      uiState.uiHidden = !uiState.uiHidden;
      break;
    case 'CLEARCHAT':
      uiState.chat.messages = [];
      break;
    case 'LEADERBOARD':
      uiState.leaderboardHidden = !uiState.leaderboardHidden;
      break;
  }
}

/** Turns the laser pointer on or off: saved, mirrored for the menu's switch, and shown or hidden. The LASER key and the menu's switch both come here. */
export function setLaserPointer(enabled: boolean) {
  setSettings({ game: { ...settings.game, laserPointer: enabled } });
  uiState.gameSettings.laserPointer = enabled;
  visibilityLaserPointer(enabled);
}

/** LASER keybind handler. */
function toggleLaserPointer() {
  setLaserPointer(!(settings.game?.laserPointer ?? GAME_DEFAULTS.laserPointer));
}

/** Returns whether the chat box is currently visible. */
export function chatBoxOpen() {
  return uiState.chat.isInputVisible;
}

/** Initializes minimap sprites and sets initial state. */
export function setupMinimap() {
  gui.minimap = texturesInit('minimap');
  gui.minimap_box = texturesInit('minimapBox');
  gui.minimap_self = texturesInit('minimapSelf');
  resizeMinimap();
  visibilityMinimap(false);
}

/** Shows or hides minimap elements. */
function visibilityMinimap(isVisible: boolean) {
  if (!gui.minimap || !gui.minimap_box) return;

  gui.minimap.visible = isVisible;
  gui.minimap_box.visible = isVisible;
  if (gui.minimap_self) gui.minimap_self.visible = isVisible;
}

/** Portrait: distance from the top edge to the minimap's top, clearing the top
 *  bar + a notch / Dynamic Island (matches the 64px HUD top offset used on touch). */
const MINIMAP_PORTRAIT_TOP_MARGIN = 64;
/** Mobile landscape bottom margin — matches the default minimap padding. */
const MINIMAP_MOBILE_BOTTOM_MARGIN = MINIMAP_PADDING_DEFAULT;

/**
 * Sizes the HUD for a mobile screen on every resize, as legacy's
 * modifyConfigIfMobile in Graphics did: the scoreboard shows 8 rows, 5 on a
 * phone (a side under 600 px) and 7 on a tablet (600 to 1024), the minimap
 * shrinks to 160 px on a phone, and its padding centres it.
 */
export function applyMobileLayout() {
  if (device.mobile) {
    const orientation = viewport.width > viewport.height ? 'landscape' : 'portrait';

    // Detect if device is a phone based on screen size
    const phone =
      (viewport.width <= 599 && orientation === 'portrait') || (viewport.height <= 599 && orientation === 'landscape');

    // Detect if device is a tablet based on screen size
    const tablet =
      (viewport.width >= 600 && viewport.width <= 1024 && orientation === 'portrait') ||
      (viewport.height >= 600 && viewport.height <= 1024 && viewport.width <= 1024 && orientation === 'landscape');

    // Set default mobile scoreboard size
    maxScoreboard = 8;

    // Adjust settings for phones
    if (phone) {
      minimapLayoutState.size = 160;
      maxScoreboard = 5;
    }

    // Adjust settings for tablets
    if (tablet) {
      maxScoreboard = 7;
    }

    // Center minimap horizontally
    minimapLayoutState.paddingX = viewport.width / 2 - minimapLayoutState.size / 2;
  }
}

/** Repositions minimap, viewport box, mobs, and UIState rect to match screen */
export function resizeMinimap() {
  if (!gui.minimap || !gui.minimap_box) return;

  const scaledSize = minimapLayout.size * minimapLayout.scale;

  // On mobile, center minimap horizontally (both orientations).
  if (device.mobile) {
    minimapLayoutState.paddingX = viewport.width / 2 - scaledSize / 2;

    // Portrait: lift the minimap to the top-centre. In portrait both thumbs sit
    // at the bottom (dynamic stick bottom-left, action cluster bottom-right) and
    // the usual bottom-centre minimap lands between them, under the hands. The
    // top-centre is clear of both thumb arcs (the top-bar centre is empty).
    // Landscape keeps the default bottom margin — bottom-centre is fine there.
    // Both branches set paddingY every call, so it self-heals on rotation.
    minimapLayoutState.paddingY =
      viewport.height > viewport.width
        ? viewport.height - MINIMAP_PORTRAIT_TOP_MARGIN - scaledSize / 2
        : MINIMAP_MOBILE_BOTTOM_MARGIN;
  }

  gui.minimap.scale.set(scaledSize / 512);
  gui.minimap.position.set(viewport.width - minimapLayout.paddingX, viewport.height - minimapLayout.paddingY);
  gui.minimap_box.scale.set(
    0.03 + (2 * scaledSize * (viewport.width / viewport.scale / MAP_WIDTH)) / 64,
    0.03 + (scaledSize * (viewport.height / viewport.scale / MAP_HEIGHT)) / 64,
  );

  for (const mob of minimapMobs.values()) {
    updateMinimapMob(mob);
  }

  // Mob positions just moved; the tracked-player ring follows a cached mob
  // position, so resync it on the same tick to avoid the ring lagging the
  // dot for up to 5 seconds (until the next scoreboard refresh).
  updateTrackedSprite();

  const height = scaledSize / 2;
  const top = viewport.height - minimapLayout.paddingY - height;
  // Viewport coordinates: viewport.offsetX bridges canvas→viewport space when
  // squaredScene centers a width-capped canvas (0 otherwise). Consumers
  // (alt-click autopilot mapping, DOM-positioned components) work in
  // viewport space.
  const left = viewport.offsetX + viewport.width - minimapLayout.paddingX - scaledSize;
  // A fresh object wakes every bound component, and zoom drags land here per
  // input frame, so only publish when the rect moved.
  const rect = uiState.minimapRect;
  if (rect.top !== top || rect.left !== left || rect.width !== scaledSize || rect.height !== height) {
    uiState.minimapRect = { top, left, width: scaledSize, height };
  }

  gamesUpdate(true);
}

/** Hit-tests a screen point against the minimap bounds (2:1 aspect ratio) */
function isPointOverMinimap(x: number, y: number) {
  const scaledSize = minimapLayout.size * minimapLayout.scale;
  // x/y are viewport (client) coords; viewport.offsetX maps the canvas-space
  // minimap bounds into viewport space (0 unless squaredScene is centering
  // a width-capped canvas).
  const right = viewport.offsetX + viewport.width - minimapLayout.paddingX;
  const bottom = viewport.height - minimapLayout.paddingY;
  const left = right - scaledSize;
  const top = bottom - scaledSize / 2;

  return x >= left && x <= right && y >= top && y <= bottom;
}

/** Sets minimap scale (clamped to min/max) and resizes, without persisting —
 *  the menu slider's drag preview. setMinimapScale commits. */
export function applyMinimapScale(scale: number) {
  const clamped = Math.max(MINIMAP_SCALE_MIN, Math.min(MINIMAP_SCALE_MAX, scale));
  // A slider drag resends the current value on most moves; nothing to lay out then.
  if (clamped === minimapLayout.scale) return;
  minimapLayoutState.scale = clamped;
  uiState.displaySettings.minimapScale = clamped;
  resizeMinimap();
}

/** Sets minimap scale (clamped to min/max), resizes, and persists */
export function setMinimapScale(scale: number) {
  applyMinimapScale(scale);

  setSettings({
    display: {
      ...settings.display,
      minimapScale: minimapLayout.scale,
    },
  });
}

/** Resets minimap scale to default (1.0) */
function resetMinimapScale() {
  setMinimapScale(MINIMAP_SCALE_DEFAULT);
}

/** True when a panel/drawer overlays the screen — minimap interactions are
 *  window-level listeners gated only by cursor-over-minimap-rect, so without
 *  this guard they fire "through" an open menu (eating its wheel scroll,
 *  resizing the minimap underneath, or even starting autopilot on alt+click). */
function minimapInteractionBlocked(): boolean {
  return uiState.activePanel !== null;
}

/** Scales minimap on wheel scroll (~10% per tick) */
function handleMinimapWheel(e: WheelEvent) {
  if (minimapInteractionBlocked()) return;
  if (!gui.minimap?.visible) return;
  if (!isPointOverMinimap(e.clientX, e.clientY)) return;

  const direction = e.deltaY < 0 ? 1 : -1;
  const next = Math.max(MINIMAP_SCALE_MIN, Math.min(MINIMAP_SCALE_MAX, minimapLayout.scale + direction * 0.1));
  // At either limit a trackpad stream would otherwise persist settings per event.
  if (next === minimapLayout.scale) return;
  setMinimapScale(next);
}

/** Resets minimap scale on double-click over the minimap */
function handleMinimapDblClick(e: MouseEvent) {
  if (minimapInteractionBlocked()) return;
  if (!gui.minimap?.visible) return;
  if (!isPointOverMinimap(e.clientX, e.clientY)) return;

  resetMinimapScale();
}

/** What the chat says when an alt+click cannot start the autopilot; SWAM's line for a bad destination. */
const AUTOPILOT_REFUSALS: Readonly<Record<AutoPilotFailure, string>> = {
  'not alive': 'Autopilot: not while dead or spectating.',
  'already at destination': 'Autopilot: already there.',
  'start out of bounds': 'Autopilot: Invalid destination.',
  'destination out of bounds': 'Autopilot: Invalid destination.',
  'no walkable cell near start': 'Autopilot: Invalid destination.',
  'no walkable cell near destination': 'Autopilot: Invalid destination.',
  'no path found': 'Autopilot: Invalid destination.',
};

/** Alt+click on minimap → start the autopilot toward that map position. */
function handleMinimapAltClick(e: MouseEvent) {
  if (minimapInteractionBlocked()) return;
  if (!e.altKey) return;
  if (e.button !== 0) return;
  if (!gui.minimap?.visible) return;
  if (!isPointOverMinimap(e.clientX, e.clientY)) return;

  const rect = uiState.minimapRect;
  if (!rect) return;

  const normalizedX = (e.clientX - rect.left) / rect.width;
  const normalizedY = (e.clientY - rect.top) / rect.height;
  const worldX = -MAP_CENTER_X + normalizedX * MAP_WIDTH;
  const worldY = -MAP_CENTER_Y + normalizedY * MAP_HEIGHT;

  e.preventDefault();
  e.stopPropagation();

  const result = startAutoPilot(worldX, worldY);
  if (!result.ok) {
    addChatMessage(AUTOPILOT_REFUSALS[result.reason]);
  }
}

/** Restores saved minimap scale and attaches wheel/dblclick/click listeners */
export function setupMinimapScaling() {
  if (settings.display?.minimapScale != null) {
    minimapLayoutState.scale = settings.display.minimapScale;
    uiState.displaySettings.minimapScale = minimapLayout.scale;
    resizeMinimap();
  }

  // Passive: the body never scrolls (overflow hidden), so there was nothing to
  // prevent, and a non-passive window listener made every wheel event in the
  // chat and menus wait on the main thread.
  window.addEventListener('wheel', handleMinimapWheel, { passive: true });
  window.addEventListener('dblclick', handleMinimapDblClick);
  window.addEventListener('click', handleMinimapAltClick);
}

/** Initializes HUD health and energy arc sprites with masks */
export function setupHUD() {
  // Health arc (right side)
  gui.hudHealth_shadow = texturesInit('hudHealth_shadow');
  gui.hudHealth = texturesInit('hudHealth');
  gui.hudHealth_mask = texturesInit('hudHealth_mask');
  gui.hudHealth.rotation = -1.5;
  gui.hudHealth.position.set(330, 174);
  gui.hudHealth_mask.position.set(330, 174);
  gui.hudHealth.mask = gui.hudHealth_mask;

  // Energy arc (left side)
  gui.hudEnergy_shadow = texturesInit('hudEnergy_shadow');
  gui.hudEnergy = texturesInit('hudEnergy');
  gui.hudEnergy_mask = texturesInit('hudEnergy_mask');
  gui.hudEnergy.position.set(-250, 174);
  gui.hudEnergy_mask.position.set(-250, 174);
  gui.hudEnergy.mask = gui.hudEnergy_mask;

  // Fresh sprites: forget the last drawn values and redraw both textures.
  lastHudHealth = -1;
  lastHudEnergy = -1;
  lastHudEnergyTint = -1;
  markHudStale();

  resizeHUD();
  visibilityHUD(false);
}

/**
 * Sets the visibility of HUD elements
 *
 * @param isVisible - Whether to show or hide the HUD
 */
export function visibilityHUD(isVisible: boolean) {
  if (gui.hudHealth_shadow) {
    gui.hudHealth_shadow.visible = isVisible;
  }
  if (gui.hudHealth) {
    gui.hudHealth.visible = isVisible;
  }
  if (gui.hudEnergy_shadow) {
    gui.hudEnergy_shadow.visible = isVisible;
  }
  if (gui.hudEnergy) {
    gui.hudEnergy.visible = isVisible;
  }
  // The arcs live inside the containers drawn to the HUD textures, so a
  // visibility change needs a redraw (to blank them, or to bring them back).
  markHudStale();
}

/**
 * Repositions HUD shadow and output sprites to match screen dimensions.
 * The fill/mask children are rendered to texture so they stay in local space.
 */
export function resizeHUD() {
  const hudScale = 0.5 * viewport.scale;
  const healthX = viewport.halfWidth - 30 * viewport.scale;
  const energyX = viewport.halfWidth + 30 * viewport.scale;
  const y = viewport.halfHeight;

  // Shadow sprites (in ui0 layer, need screen positioning)
  if (gui.hudHealth_shadow) {
    gui.hudHealth_shadow.scale.set(hudScale);
    gui.hudHealth_shadow.position.set(healthX, y);
  }
  if (gui.hudEnergy_shadow) {
    gui.hudEnergy_shadow.scale.set(hudScale);
    gui.hudEnergy_shadow.position.set(energyX, y);
  }

  // Rendered output sprites (display the render textures on screen)
  if (gui.hudSpriteHealth) {
    gui.hudSpriteHealth.scale.set(hudScale);
    gui.hudSpriteHealth.position.set(healthX, y);
  }
  if (gui.hudSpriteEnergy) {
    gui.hudSpriteEnergy.scale.set(hudScale);
    gui.hudSpriteEnergy.position.set(energyX, y);
  }
}

// ============================================================================
// LASER POINTER
// ============================================================================

/** Laser pointer Graphics object */
let laserPointer: PixiGraphics | null = null;

/**
 * Sets up the laser pointer visual element.
 * Creates a line from screen center extending outward.
 */
export function setupLaserPointer() {
  laserPointer = new PixiGraphics();

  // Draw line extending from origin (will be positioned at screen center).
  // pixelLine renders a native GPU line — uniformly 1 pixel along its whole
  // length at any rotation, unlike a triangulated 1px stroke which rasterizes
  // unevenly without MSAA (the renderer runs antialias-off). Same technique
  // as the hitshapes sea grid.
  const length = Math.max(viewport.width, viewport.height) * 1.5;
  laserPointer.moveTo(0, 0);
  laserPointer.lineTo(length, 0);
  laserPointer.stroke({ width: 1, color: 0xffffff, alpha: 0.25, pixelLine: true });

  // Position at screen center
  laserPointer.position.set(viewport.halfWidth, viewport.halfHeight);

  // Add to UI layer
  layers.ui0.addChild(laserPointer);

  // Start hidden
  laserPointer.visible = false;
}

/**
 * Updates laser pointer rotation to match player rotation.
 * @param rotation - Player rotation in radians
 */
export function updateLaserPointer(rotation: number) {
  if (!laserPointer || !laserPointer.visible) {
    return;
  }

  // Offset by -π/2 because line points right (0) but ship points up (0)
  laserPointer.rotation = rotation - HALF_PI;
}

/**
 * Resizes and repositions the laser pointer for new screen dimensions.
 */
export function resizeLaserPointer() {
  if (!laserPointer) {
    return;
  }

  // Redraw with new length (pixelLine for uniform 1px rendering — see setup)
  const length = Math.max(viewport.width, viewport.height) * 1.5;
  laserPointer.clear();
  laserPointer.moveTo(0, 0);
  laserPointer.lineTo(length, 0);
  laserPointer.stroke({ width: 1, color: 0xffffff, alpha: 0.25, pixelLine: true });

  // Reposition to screen center
  laserPointer.position.set(viewport.halfWidth, viewport.halfHeight);
}

/**
 * Sets the visibility of the laser pointer.
 * @param isVisible - Whether to show or hide the laser pointer
 */
export function visibilityLaserPointer(isVisible: boolean) {
  if (laserPointer) {
    laserPointer.visible = isVisible;
  }
}

/**
 * Sends command to server to respawn with selected aircraft type
 *
 * @param type - Aircraft type (PlaneType enum: Predator..Prowler)
 */
export function selectAircraft(type: PlaneType) {
  networkSendCommand('respawn', String(type));
}

/**
 * Updates UI to show which aircraft is currently selected
 *
 * @param type - Aircraft type (PlaneType enum: Predator..Prowler)
 */
export function aircraftSelected(type: PlaneType) {
  // Update UIState for Lit components
  uiState.selectedAircraft = type;
}

/** Buffers a kill notification for the player destroying another player */
export function killed(victim: Player) {
  const freshStreak = clock.time - lastTimePlayerWasKilled > 1500 || listOfPlayersKills.length >= 6;
  if (freshStreak) {
    listOfPlayersKills = [];
  }
  // Two kills can land before the score packet; a reset by the first must
  // survive the second overwriting the pending notice.
  const pendingFresh = deathNotice !== false && deathNotice.freshStreak;

  lastTimePlayerWasKilled = clock.time;
  listOfPlayersKills.push({
    name: victim.name,
    flag: victim.flag,
    level: victim.level,
    bot: victim.bot,
  });

  deathNotice = {
    type: 'killed',
    players: listOfPlayersKills.map((p) => ({ ...p })),
    duration: 3000,
    freshStreak: freshStreak || pendingFresh,
  };
}

/** Buffers a death notification for the player being destroyed by another player */
export function killedBy(killer: Player) {
  deathNotice = {
    type: 'destroyed',
    players: [{ name: killer.name, flag: killer.flag, level: killer.level, bot: killer.bot }],
    duration: 3000,
    freshStreak: false,
  };
}

/** A server error that shows a notification, and whether it ends the session. */
interface ErrorNotice {
  type: GameNotification['type'];
  title: string;
  /** Static text, or built when the error arrives for messages that read live state. */
  message: string | (() => string);
  showReconnect: boolean;
  timeoutMs: number;
  /** The server has dropped us, so Network gets told as well. */
  disconnect: boolean;
}

function bannedMessage(reason: string) {
  return `You have been banned from this server (${session.serverId ?? ''})${reason}`;
}

function afkMessage() {
  const minutes = session.serverConfig?.afk;
  if (!minutes) return 'AFK time exceeded the server limit';
  return `AFK for more than ${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

/** Notification per server error code. Codes 4, 30 and 100 are handled in errorHandler itself. */
const ERROR_NOTICES: Readonly<Record<number, ErrorNotice>> = {
  1: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: 'Packet flooding detected',
    showReconnect: true,
    timeoutMs: 20000,
    disconnect: true,
  },
  2: {
    type: 'alert',
    title: 'BANNED',
    message: () => bannedMessage(' for packet flooding'),
    showReconnect: false,
    timeoutMs: 20000,
    disconnect: true,
  },
  3: {
    type: 'alert',
    title: 'BANNED',
    message: () => bannedMessage(''),
    showReconnect: false,
    timeoutMs: 20000,
    disconnect: true,
  },
  5: {
    type: 'alert',
    title: 'RESPAWN',
    message: 'Full health and 2 seconds of inactivity required',
    showReconnect: false,
    timeoutMs: 3000,
    disconnect: false,
  },
  6: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: afkMessage,
    showReconnect: true,
    timeoutMs: 7200000,
    disconnect: true,
  },
  7: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: 'You have been kicked out',
    showReconnect: true,
    timeoutMs: 20000,
    disconnect: true,
  },
  8: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: 'Invalid login data',
    showReconnect: false,
    timeoutMs: 20000,
    disconnect: true,
  },
  9: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: 'Incorrect protocol level — please clear your browser cache and refresh',
    showReconnect: false,
    timeoutMs: 20000,
    disconnect: true,
  },
  10: {
    type: 'alert',
    title: 'BANNED',
    message: 'Account banned',
    showReconnect: false,
    timeoutMs: 20000,
    disconnect: true,
  },
  11: {
    type: 'alert',
    title: 'DISCONNECTED',
    message: 'Account already logged in',
    showReconnect: true,
    timeoutMs: 20000,
    disconnect: true,
  },
  12: {
    type: 'alert',
    title: 'RESPAWN',
    message: 'Cannot respawn or change aircraft in a Battle Royale game',
    showReconnect: false,
    timeoutMs: 3000,
    disconnect: false,
  },
  13: {
    type: 'alert',
    title: 'SPECTATE',
    message: 'Full health and 2 seconds of inactivity required',
    showReconnect: false,
    timeoutMs: 3000,
    disconnect: false,
  },
  20: {
    type: 'information',
    title: 'UPGRADE',
    message: 'Not enough upgrade points',
    showReconnect: false,
    timeoutMs: 3000,
    disconnect: false,
  },
  31: {
    type: 'alert',
    title: 'THROTTLED',
    message: 'Flag change too fast',
    showReconnect: false,
    timeoutMs: 2000,
    disconnect: false,
  },
};

/** Handles server error messages — sets UIState for Lit notification component */
export function errorHandler(msg: ServerErrorMessage) {
  switch (msg.error) {
    case SERVER_ERRORS.UNKNOWN_ERROR:
      // The original client went home on this one with no UI, so this does too
      networkReceivedError(msg.error);
      gamesRedirRoot();
      return;
    case SERVER_ERRORS.CHAT_SPAMMING:
      addChatMessage('Chat throttled to prevent spamming');
      return;
    case SERVER_ERRORS.UNKNOWN_COMMAND:
      addChatMessage('Unknown command');
      return;
  }

  const notice = ERROR_NOTICES[msg.error];
  if (!notice) return;

  setGameNotification({
    type: notice.type,
    title: notice.title,
    message: typeof notice.message === 'string' ? notice.message : notice.message(),
    showReconnect: notice.showReconnect,
    timeoutMs: notice.timeoutMs,
  });
  if (notice.disconnect) {
    networkReceivedError(msg.error);
  }
}

/** Routes a command reply: CHAT to the chat as a system line, DEBUG to the popup as formatted JSON */
export function showCommandReply(msg: ServerCommandReplyMessage) {
  if (msg.type === COMMAND_REPLY_TYPES.CHAT) {
    // e.g. "Too frequent /say usage. Message discarded." — the server named the
    // reason, so the watchdog shouldn't guess at it a second time.
    cancelSayEchoWatch();
    addChatMessage(msg.text);
  } else {
    // ab-server JSON-encodes every non-chat reply. A malformed one shows as
    // sent rather than throwing out of the packet handler.
    let json = msg.text;
    try {
      json = JSON.stringify(JSON.parse(msg.text), null, '    ');
    } catch {
      // not JSON: keep the raw text
    }
    uiState.debugPopup = { json };
  }
}

/** Updates the HUD health and energy arc sprites (rotation = fill, tint = color) */
export function updateHUD(health: number, energy: number, player?: Player) {
  if (!gui.hudHealth || !gui.hudEnergy) {
    return;
  }

  health = toolsClamp(health, 0, 1);
  energy = toolsClamp(energy, 0, 1);

  // Each arc is redrawn into its texture only when its inputs moved.
  if (health !== lastHudHealth) {
    lastHudHealth = health;
    // Arc fill amount via rotation
    gui.hudHealth.rotation = -1.1 * (1 - health);
    // Health gradient: red -> yellow -> green (vanilla parity — these constants
    // previously carried single-hex-digit port typos that turned the gradient
    // red -> beige -> blue).
    gui.hudHealth.tint =
      health > 0.5
        ? toolsColorLerp(0xcdcd2c, 0x278c29, 2 * (health - 0.5))
        : toolsColorLerp(0xba2d2d, 0xcdcd2c, 2 * health);
    hudHealthStale = true;
  }

  // Energy tint: blue, darker warning shade when below ship's energyLight threshold
  const energyTint = player !== undefined && energy < SHIPS[player.type].energyLight ? 0x2b5c9b : 0x337ee5;
  if (energy !== lastHudEnergy || energyTint !== lastHudEnergyTint) {
    lastHudEnergy = energy;
    lastHudEnergyTint = energyTint;
    gui.hudEnergy.rotation = Math.PI + 1.1 * (1 - energy);
    gui.hudEnergy.tint = energyTint;
    hudEnergyStale = true;
  }
}

/**
 * Minimizes the chat box and shows the maximize button
 * Closes the chat input if visible and resets unread message count
 */
function minimizeChat(event?: MouseEvent) {
  if (uiState.chat.isMinimized) {
    return;
  }

  if (uiState.chat.isInputVisible) {
    toggleChatBox();
  }

  uiState.chat.isMinimized = true;
  uiState.chat.unreadCount = 0;

  event?.stopPropagation();
}

/**
 * Maximizes the chat box and scrolls to the bottom
 * Hides the maximize button and shows the chat box
 */
function maximizeChat() {
  if (!uiState.chat.isMinimized) {
    return;
  }

  uiState.chat.isMinimized = false;
}

/**
 * Starts score polling (initial fetch + 5-second interval).
 * Clears any existing interval first.
 */
export function startScorePolling() {
  // Seed an empty board so the panel mounts with its frame and plays its
  // entrance at once, as legacy's panel did. The first reply fills the rows a
  // round trip later. Without the seed the panel's first render was empty,
  // its entrance found nothing to animate, and it popped in late.
  uiState.scoreboard ??= {
    gameMode: uiState.gameType ?? 'ffa',
    roomName: session.roomName,
    players: [],
    playerCount: 0,
    mvp: null,
  };
  networkGetScores();
  if (getScoresIntervalId != null) {
    clearInterval(getScoresIntervalId);
  }
  getScoresIntervalId = window.setInterval(networkGetScores, 5000);
}

/** Stops score polling and clears scoreboard state. */
export function stopScorePolling() {
  if (getScoresIntervalId != null) {
    clearInterval(getScoresIntervalId);
    getScoresIntervalId = null;
  }
  uiState.scoreboard = null;
}

/** Toggles the score panel visibility */
function toggleScore() {
  if (uiState.activePanel === 'score') {
    setActivePanel(null);
  } else {
    closeAllPanels('score');
    startScorePolling();
    setActivePanel('score');
  }
}

/**
 * Generates an invite link for the current game session and stores it in uiState.
 */
export function generateInviteLink() {
  const [region, room] = (session.serverId ?? '').split('-');
  const url = new URL(location.href);
  url.search = '';
  // Nor the page's own hash: #nomobile or #forcemobile (testing switches) or
  // #reload (after a protocol-update reload) would carry over to the friend
  url.hash = '';
  if (region) url.searchParams.set('region', region);
  if (room) url.searchParams.set('room', room);
  if (session.myName) url.searchParams.set('inviter', session.myName);
  uiState.inviteLink = url.toString();
}

/**
 * Toggles the invite friends panel visibility
 */
function toggleInvite() {
  if (uiState.activePanel === 'invite') {
    setActivePanel(null);
  } else {
    closeAllPanels('invite');
    generateInviteLink();
    setActivePanel('invite');
  }
}

/**
 * The one writer of `uiState.activePanel`. Leaving the score panel stops its
 * polling here, so no open-or-close path can strand the 5s score requests
 * (the EMOTES key and `/emotes` once did, by writing the field directly).
 */
export function setActivePanel(panel: PanelType | null): void {
  const current = uiState.activePanel;
  if (current === panel) return;
  if (current === 'score') {
    stopScorePolling();
  }
  // How to Play open again, by whatever route, has nothing left for the next login to bring back
  if (panel === 'help') {
    helpAtDisconnect = null;
  }
  uiState.activePanel = panel;
}

/** EMOTES key and the `/emotes` command: opens through closeAllPanels like every other panel. */
export function toggleEmotes(): void {
  if (uiState.activePanel === 'emotes') {
    setActivePanel(null);
  } else {
    closeAllPanels('emotes');
    setActivePanel('emotes');
  }
}

/**
 * Closes all open panels except the specified one.
 *
 * @param exceptPanel - Panel to keep open (e.g., "score", "help")
 */
export function closeAllPanels(exceptPanel?: string) {
  // Close active Lit panel
  if (uiState.activePanel !== null && uiState.activePanel !== exceptPanel) {
    setActivePanel(null);
  }

  // Transient float UI (top-bar popovers, player context menu) never
  // participates in exceptPanel, so always clear it: opening or closing any
  // full panel should dismiss a stray popover/context menu rather than leave
  // it lingering behind. Centralized here because every panel-transition path
  // funnels through closeAllPanels — keeping per-call-site clears in sync was
  // already drifting (scoreboard-button cleared both, room-header only one).
  // The debug popup (a server command's JSON reply) is the same kind of float
  // UI, so Escape, a tap on the game world (touchCloseAll) and opening a panel
  // close it too.
  uiState.openTopBarPanel = null;
  uiState.contextMenu = null;
  uiState.debugPopup = null;
  // Legacy's closeAllPanels also hid #custom-msg, the one panel behind the
  // match results and the level-up notice.
  uiState.btrMatchResult = null;
  uiState.ctfMatchResult = null;
  uiState.levelUp = null;
}

/** How to Play as a lost connection closed it, for the next login to reopen; null when it was not open. */
let helpAtDisconnect: { condensed: boolean } | null = null;

/**
 * Clears the screen for the disconnect notice. Any open panel closes, since its
 * backdrop would hide the notice and take the click meant for its RECONNECT
 * button. How to Play comes back at the next login: a new player's first game
 * opens it by itself and marks it seen, so the drop would cost them the
 * introduction.
 */
export function connectionLost() {
  helpAtDisconnect = uiState.activePanel === 'help' ? { condensed: uiState.helpCondensed } : null;
  closeAllPanels();
}

/** Opens the menu drawer at one of its pages, closing any other panel or
 *  popover first so nothing floats above it. */
export function openMainMenu(page: MenuPage = 'main') {
  closeAllPanels('mainmenu');
  uiState.menuPage = page;
  setActivePanel('mainmenu');
}

/** The flag's title: its name with the code in parentheses. Every flag has a name, generated with the codes. */
function getFlagLabel(flagCode: FlagCode): string {
  return `${flagName(flagCode)} (${flagCode})`;
}

/**
 * Updates the UIState scoreboard data for the Lit score panel.
 *
 * Transforms server score data into plain UIState objects for reactive rendering.
 * Called from Network.ts when score data is received.
 *
 * @param msg - Score data from server for current game mode
 */
export function updateScoreboardUIState(
  msg: ServerScoreDetailedMessage | ServerScoreDetailedBtrMessage | ServerScoreDetailedCtfMessage,
) {
  // Only update if score panel is active
  if (uiState.activePanel !== 'score') return;

  // Sort scores by score value (descending)
  const scores = [...msg.scores].sort((a, b) => b.score - a.score);

  // Determine game mode from packet type
  let gameMode: GameMode = 'ffa';
  if (msg.c === SERVER_PACKETS.SCORE_DETAILED_BTR) gameMode = 'btr';
  else if (msg.c === SERVER_PACKETS.SCORE_DETAILED_CTF) gameMode = 'ctf';

  // Transform to plain data objects
  const players: ScoreboardPlayer[] = [];
  let mvpPlayer: ScoreboardPlayer | null = null;
  let mvpMetric = -1;

  for (let i = 0; i < scores.length; i++) {
    const score = scores[i];
    if (!score) continue;

    const player = playersGet(score.id);
    if (!player) continue;

    // Determine spectating status (BTR uses score.alive, others use player.isSpectating)
    const isSpectating = gameMode === 'btr' ? 'alive' in score && !score.alive : player.isSpectating() && !player.bot;

    const entry: ScoreboardPlayer = {
      id: score.id,
      rank: i + 1,
      name: player.name,
      flag: player.flag,
      flagLabel: getFlagLabel(player.flag),
      isBot: player.bot,
      team: player.team,
      isSpectating,
      isMe: player.me(),
      kills: score.kills,
      deaths: score.deaths,
      score: score.score,
      level: score.level,
      ping: score.ping,
      damage: 'damage' in score ? score.damage : undefined,
      captures: 'captures' in score ? score.captures : undefined,
      wins: 'wins' in score ? score.wins : undefined,
    };

    players.push(entry);

    // Track MVP metric (damage for FFA, captures for CTF, kills for BTR)
    const metric = gameMode === 'ffa' ? (entry.damage ?? 0) : gameMode === 'ctf' ? (entry.captures ?? 0) : entry.kills;
    if (metric > mvpMetric) {
      mvpMetric = metric;
      mvpPlayer = entry;
    }
  }

  // Build MVP data
  let mvp: ScoreboardMvp | null = null;
  if (mvpPlayer && mvpMetric > 0) {
    let metricLabel: string;
    if (gameMode === 'ffa') {
      metricLabel = 'damage';
    } else if (gameMode === 'ctf') {
      metricLabel = mvpMetric === 1 ? 'capture' : 'captures';
    } else {
      metricLabel = mvpMetric === 1 ? 'kill' : 'kills';
    }

    mvp = {
      name: mvpPlayer.name,
      flag: mvpPlayer.flag,
      value: mvpMetric,
      metricLabel,
    };
  }

  uiState.scoreboard = {
    gameMode,
    roomName: session.roomName,
    players,
    // Every row the server sent, as legacy counted, even one whose player we don't know yet
    playerCount: scores.length,
    mvp,
  };
}

/**
 * Checks if a player is currently ignored.
 *
 * @param playerID - The ID of the player to check
 * @returns True if the player is ignored
 */
export function isPlayerIgnored(playerID: number): boolean {
  return ignoredPlayerIds.has(playerID);
}

/**
 * Adds a player to the ignore list.
 *
 * Ignoring a player hides their chat messages.
 *
 * @param playerID - The ID of the player to ignore
 */
export function chatIgnore(playerID: number): void {
  const player = playersGet(playerID);

  if (player == null) {
    return;
  }

  if (playerID === session.myID) {
    return;
  }

  if (ignoredPlayerIds.has(player.id)) {
    addChatMessage(
      `Player ${player.name} is already ignored  \u2022  To unignore type /unignore ${escapePlayerName(player.name)}`,
      false,
    );
    return;
  }

  ignoredPlayerIds.add(player.id);
  syncIgnoredToUIState();
  addChatMessage(
    `Ignoring player ${player.name}  \u2022  To unignore type /unignore ${escapePlayerName(player.name)}`,
    false,
  );
}

/**
 * Removes a player from the ignore list.
 *
 * @param player - The player to unignore
 */
export function chatUnignore(player: Player): void {
  if (!ignoredPlayerIds.has(player.id)) {
    return;
  }

  ignoredPlayerIds.delete(player.id);
  syncIgnoredToUIState();
  addChatMessage(`Removed player ${player.name} from ignore list`, false);
}

/** Sync the internal ignore set to UIState for UI reactivity. */
function syncIgnoredToUIState(): void {
  uiState.ignoredPlayerIds = [...ignoredPlayerIds];
}

/**
 * Votes to mute a player.
 *
 * Calculates and displays how many votes are required to mute the player.
 *
 * @param player - The player to vote mute
 */
export function chatVotemute(player: Player): void {
  if (player.id === session.myID) {
    return;
  }

  networkVoteMute(player.id);

  const [, totalPlayers] = playersCount();
  const votesRequired = Math.floor(Math.sqrt(totalPlayers)) + 1;
  addChatMessage(`Voted to mute ${player.name}  \u2022  ${votesRequired} total votes are required`, false);
}

/**
 * Displays a message when a vote to mute a player has passed.
 *
 * @param player - The player who has been muted by vote
 */
export function chatVotemutePass(player: Player): void {
  addChatMessage(`The vote to mute ${player.name} has passed`, false);
}

/**
 * Displays a message informing the local player they have been muted.
 */
export function chatMuted(): void {
  addChatMessage('You have been muted');
}

/** Drops the current upgrade powerup. */
function dropUpgrade() {
  networkSendCommand('upgrades', 'drop');
}

/** Drops the flag in CTF mode. */
function dropFlag() {
  networkSendCommand('drop', '');
}

/**
 * Selects an upgrade option.
 *
 * @param upgradeIndex - The upgrade slot index (1-4)
 */
export function selectUpgrade(upgradeIndex: 1 | 2 | 3 | 4) {
  networkSendCommand('upgrade', String(upgradeIndex));
}

/**
 * Updates player upgrade levels and UI displays.
 *
 * Called when the server sends upgrade data. Updates internal state,
 * UI elements, tooltips, and shows upgrade notification if applicable.
 *
 * @param levels - Tuple of [speed, defense, energy, missile] levels (0-5)
 * @param msgUpgrades - Number of available upgrade points
 * @param msgTypeId - Type of upgrade just applied (undefined/0 if none, 1-4 for specific upgrade)
 */
export function updateUpgrades(
  levels: [number, number, number, number],
  msgUpgrades?: number,
  msgTypeId?: PLAYER_UPGRADE_TYPES,
) {
  // Update each upgrade level directly from the tuple
  playerUpgrades.speed = levels[0];
  playerUpgrades.defense = levels[1];
  playerUpgrades.energy = levels[2];
  playerUpgrades.missile = levels[3];

  // Show upgrade notification if a specific upgrade was just applied
  if (msgTypeId != null && msgTypeId !== PLAYER_UPGRADE_TYPES.LOST) {
    const key = UPGRADE_KINDS[msgTypeId - 1];
    if (key) {
      const currentLevel = playerUpgrades[key];
      const upgradeFactor = UPGRADES[key].factor[currentLevel];
      if (upgradeFactor !== undefined) {
        const percentIncrease = Math.round(100 * (upgradeFactor - 1));
        const message = `+${percentIncrease}% ${UPGRADE_DISPLAY_NAMES[key]}`;

        setGameNotification({ type: 'information', title: 'UPGRADE', message, showReconnect: false, timeoutMs: 3000 });
        soundPlayerUpgrade();
      }
    }
  }

  // Update upgrade points display
  if (msgUpgrades !== undefined) {
    // A drop arrives via PLAYER_UPGRADE (LOST), not newScore — fold that loss
    // into a live kill notice's tally too (mirrors newScore) so a drop +
    // re-collect can't inflate the "+N upgrades" popup. Clamp at 0. Spending a
    // point (types 1-4) lowers the count as well but is not a loss, so it
    // leaves the tally alone.
    const upgradesDelta = msgUpgrades - playerStats.upgrades;
    const lost = msgTypeId === PLAYER_UPGRADE_TYPES.LOST;
    const live = lost && playerStats.upgrades !== -1 && upgradesDelta < 0 ? liveKillNotice() : null;
    if (live) {
      live.upgradesChange = Math.max(0, live.upgradesChange + upgradesDelta);
    }
    playerStats.upgrades = msgUpgrades;
  }

  // Update UIState for Lit components
  uiState.upgradeLevels = { ...playerUpgrades };
  uiState.availableUpgrades = playerStats.upgrades;
}

/** Resets all upgrade levels to 0. Called when starting a new game or respawning. */
function resetUpgrades() {
  updateUpgrades([0, 0, 0, 0]);
}

/** Opens the help panel, closing any other active panel first. */
function showHelp(startHidden?: boolean) {
  if (uiState.activePanel === 'help') return;
  closeAllPanels('help');
  uiState.helpCondensed = startHidden === true;
  setActivePanel('help');
}

/** Closes the help panel if it is currently open. */
export function hideHelp() {
  if (uiState.activePanel !== 'help') {
    return;
  }
  setActivePanel(null);
}

/** Toggles the help panel open or closed. */
export function toggleHelp() {
  if (uiState.activePanel === 'help') {
    hideHelp();
  } else {
    showHelp();
  }
}

/**
 * Mirrors the sound setting for the toolbar's speaker icon, and shows the
 * notice when asked. Every change to the setting comes through here, as it
 * did through legacy's updateSound, which repainted its icon: the SOUND key
 * and the toolbar button through Sound.toggle, and the saved setting through
 * applySettingsToGame. The icon had been mirrored by the toolbar button's
 * GameAPI path alone, so the key left it stale.
 */
export function updateSound(showNotification?: boolean) {
  uiState.isSoundEnabled = settings.sound ?? true;
  if (showNotification) {
    const status = settings.sound ? 'Enabled' : 'Disabled';
    setGameNotification({ type: 'alert', title: 'SOUND', message: status, showReconnect: false, timeoutMs: 3000 });
  }
}

/** Initializes the game when a player starts playing and sets up the network connection. */
export function gameStart(isFirstTime?: boolean) {
  uiState.screen = 'game';

  if (isFirstTime) {
    // A phone plays fullscreen. Its touch controls are mounted by the UI off
    // uiState.isMobile, set at detection.
    if (device.mobile) {
      graphicsToggleFullscreen();
    }

    // Show help on first play
    if (!settings.helpshown) {
      showHelp(true);
      setSettings({
        helpshown: true,
      });
    }
  }

  playerUpgrades = { ...DEFAULT_UPGRADES };
  resetUpgrades();
  hideSpectator();
  resetPowerups();
  startProwlerAlert();

  networkSetup();
}

/**
 * Resets all game state when reconnecting or switching games.
 *
 * Clears chat, player stats, upgrades, UI elements, and prepares for a new connection.
 */
export function reconnection() {
  // Reset chat state
  chatLineId = 0;

  // Reset player statistics
  playerStats = {
    upgrades: -1,
    earnings: -1,
    kills: -1,
    deaths: -1,
  };

  // Clear player state
  deathNotice = false;
  uiState.killNotice = null;
  uiState.deathNotice = null;
  // Match results run for the server's duration (13s for BTR) and a level-up
  // for 8s, so a switch mid-way would otherwise carry them into the next game.
  uiState.btrMatchResult = null;
  uiState.ctfMatchResult = null;
  uiState.levelUp = null;
  playerUpgrades = { ...DEFAULT_UPGRADES };
  ignoredPlayerIds.clear();
  uiState.ignoredPlayerIds = [];

  // Call reset functions
  resetUpgrades();
  resetPowerups();
  hideSpectator();
  stopProwlerAlert();
  cancelAutoPilot();
  cancelSpectatorOnStart();
  stopCtfMatchTimer();
  cancelSayEchoWatch();
  wipeAllMinimapMobs();
  wipeAllMessages();
  gamesWipe();

  // Clear UI elements. The plane rosters come from the same scoreboard packet
  // as the leaderboard, so they clear with it — otherwise the last CTF game's
  // counts and names sit on the plane buttons until the new game's first one.
  uiState.leaderboard = null;
  uiState.ctfPlaneRosters = null;
  // The next session starts at 0; mirror it so the HUD doesn't show the last
  // game's score until the first SCORE_UPDATE.
  uiState.score = 0;

  // The feed's player ids belong to the server being left: a click on an old
  // line would open the menu for whoever has that id on the next one.
  uiState.gameLogEntries = [];

  // Reset chat state
  uiState.chat.messages = [];
  uiState.chat.isInputVisible = false;
  uiState.chat.isMinimized = false;
  uiState.chat.unreadCount = 0;
  uiState.chat.isHintRemoved = false;
  uiState.chat.inputPrefill = null;
  uiState.debugPopup = null;
}

/** Enables HUD, minimap, and laser pointer after login. */
export function loggedIn() {
  visibilityHUD(true);
  visibilityMinimap(true);
  if (settings.game?.laserPointer ?? GAME_DEFAULTS.laserPointer) {
    visibilityLaserPointer(true);
  }
  updateHUD(1, 1);
  // The LOGIN packet brought the server's zoom cap: the saved zoom is applied
  // under it, the server told the horizon that gives, and the zoom controls
  // shown the cap
  mirrorServerZoomCap();
  graphicsApplyScaleOnly();
  networkResizeHorizon();
  noticeViewportFloor();
  if (helpAtDisconnect !== null) {
    showHelp(helpAtDisconnect.condensed);
  }
}

/**
 * Sets up all UI event handlers and texturesInitializes UI components.
 */
export function setup() {
  // Window event handlers
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('orientationchange', onWindowResize);
  window.visualViewport?.addEventListener('resize', onVisualViewportResize);
  window.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });
  window.addEventListener('focus', inputGameFocus);
  window.addEventListener('blur', inputGameBlur);
}

// Zoom constants live in ./Constants (SCALE_MIN/SCALE_MAX/SCALE_DEFAULT) —
// shared with the in-game zoom-slider, menu-drawer's zoom slider, and the
// scalingFactor default in Config.ts.

/** BACKSPACE keybind handler. Toggles between normal and alt zoom levels. */
export function toggleScaleAltMode() {
  const newMode = !settings.scalingAltMode;
  setSettings({ scalingAltMode: newMode });
  uiState.zoom.scalingAltMode = newMode;
  graphicsApplyScaleOnly();
  if (session.state === GAME_STATE_CODES.PLAYING) {
    networkResizeHorizon();
  }
}

/** ZOOMIN keybind handler. Capped here, not just on read, since the zoom
 *  sliders show the stored value. Steps from the zoom in use, so a saved
 *  zoom the server holds down zooms in from where the screen is. */
function scaleIncrease() {
  setScalingFactor(capScalingFactor(Math.min(savedScalingFactor(), Math.round(getScalingFactor())) - 200));
}

/** ZOOMOUT keybind handler. Steps from the saved zoom, which may already be past the server's cap. */
function scaleDecrease() {
  setScalingFactor(capScalingFactor(savedScalingFactor() + 200));
}

/** DEFAULTZOOM keybind handler. */
function scaleDefault() {
  setScalingFactor(SCALE_DEFAULT);
}

/** The saved zoom of the active mode within SCALE_MIN..SCALE_MAX: what the sliders show and the keys step from. */
function savedScalingFactor() {
  return capScalingFactor(
    settings.scalingAltMode ? settings.altScalingFactor || ALT_SCALE_DEFAULT : settings.scalingFactor || SCALE_DEFAULT,
  );
}

/**
 * The zoom applied to the graphics: the saved zoom, held to the server's cap.
 * The saved value stays as set, so a server with a higher cap, or a window
 * whose shape allows more, shows it in full.
 */
export function getScalingFactor() {
  return Math.min(savedScalingFactor(), serverZoomCap());
}

/**
 * The widest zoom the server allows on this viewport. ab-server caps the view
 * area a client declares (horizonX × horizonY) at sf² / 16, which in zoom
 * units is sf × (w + h) / (2√(w·h)): sf itself on a square window, about
 * 1.04 × sf at 16:9. Past it the server stops sending what the edge of the
 * screen shows, a blind band where planes and missiles vanish while the map
 * is drawn as if seen. Infinity until the LOGIN packet brings sf.
 */
function serverZoomCap() {
  const sf = session.serverConfig?.sf;
  if (sf === undefined || viewport.width <= 0 || viewport.height <= 0) return Infinity;
  return (sf * (viewport.width + viewport.height)) / (2 * Math.sqrt(viewport.width * viewport.height));
}

/** Mirrors the cap for the zoom controls, which show the saved value and say when the server holds it lower. */
function mirrorServerZoomCap() {
  const cap = serverZoomCap();
  uiState.zoom.serverCap = cap === Infinity ? null : Math.round(cap);
}

/**
 * Caps the scaling factor between minimum and maximum values.
 */
function capScalingFactor(zoom: number) {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, zoom));
}

/**
 * Schedules a graphics resize with an optional delay.
 */
function scheduleGraphicsResize(delay: number) {
  clearTimeout(delayedGraphicsResizeTimer);
  delayedGraphicsResizeTimer = window.setTimeout(resizeGraphicsNow, delay || 0);
}

/** Resizes the renderer to the viewport and re-mirrors the server's zoom cap, which depends on the viewport's
 *  shape; the squared-scene setting takes this path too, since it changes the shape without a window resize. */
export function resizeGraphicsNow(): void {
  const { width, height } = gameViewportSize();
  graphicsResizeRenderer(width, height);
  mirrorServerZoomCap();
}

/**
 * Commits a new scaling factor: persists to settings, applies to graphics, and
 * notifies the server of the new view horizon. Use for final/single-shot updates
 * (drag release, keybind, wheel, programmatic). For per-frame drag preview use
 * `applyScalingFactor`.
 */
export function setScalingFactor(zoom: number) {
  // Mirrored into uiState here, not only in GameAPI's setters: the zoom keys
  // come straight here, and the zoom sliders read uiState, so they went stale
  // and the next drag or scroll on one jumped the zoom back.
  if (settings.scalingAltMode) {
    setSettings({ altScalingFactor: zoom });
    uiState.zoom.altScalingFactor = zoom;
  } else {
    setSettings({ scalingFactor: zoom });
    uiState.zoom.scalingFactor = zoom;
  }

  graphicsApplyScaleOnly();
  if (session.state === GAME_STATE_CODES.PLAYING) {
    networkResizeHorizon();
  }
}

/**
 * Applies a scaling factor for live preview (in-memory only — no localStorage
 * write, no server horizon update). Cheap enough to call on every drag input
 * frame. Pair with `setScalingFactor` on commit.
 */
export function applyScalingFactor(zoom: number) {
  const alt = settings.scalingAltMode;
  // The slider snaps to 100-unit steps, so a slow drag resends the current
  // value on most moves; the full rescale is wasted then.
  if ((alt ? settings.altScalingFactor : settings.scalingFactor) === zoom) return;
  setSettingsUnsaved(alt ? { altScalingFactor: zoom } : { scalingFactor: zoom });
  graphicsApplyScaleOnly();
}
